import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import API from '../../../../services/API'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import DetailsBox from '../../../../components/Cards/DetailsBox'
import AnDateField from '../../../../components/DateTime/DateField'
import ListReportPDF from '../../../../components/PDF/ListReportPDF'
import usePrintChrome from '../../../../components/PDF/usePrintChrome'
import { fmtDate, GOOD, WARN } from '../../../../components/PDF/pdfKit'
import { downloadPdf } from '../../../../helpers/downloadPdf'

const testedByLabel = (check) => {
  const account = check.testedByEmployee?.userAccount
  if (account) return `${account.firstName ?? ''} ${account.lastName ?? ''}`.trim()
  return (
    [check.testedBy?.garageName, check.testedBy?.technicianName]
      .filter(Boolean)
      .join(' · ') || '—'
  )
}

// Fleet-wide list of recorded vehicle safety checks (sidebar → Safety
// Checks). The per-vehicle list lives on the vehicle's Safety Checks tab.
const SafetyChecks = () => {
  const [checks, setChecks] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [dateFrom, setDateFrom] = useState(moment().subtract(12, 'months'))
  const [dateTo, setDateTo] = useState(moment())
  const [vehicle, setVehicle] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    API.get('/settings?select=vehicleSafetyCheckFrequency').then((res) => {
      if (res.status === 200) {
        setDisabled(res.data?.vehicleSafetyCheckFrequency === 'Disabled')
      }
    })
  }, [])

  useEffect(() => {
    setLoading(true)
    setFetchError(false)
    API.get('/fleet/vehicle-safety-checks', {
      params: {
        from: dateFrom?.isValid?.() ? dateFrom.format('YYYY-MM-DD') : undefined,
        to: dateTo?.isValid?.() ? dateTo.format('YYYY-MM-DD') : undefined,
      },
    })
      .then((res) =>
        res.status === 200 ? setChecks(res.data) : setFetchError(true),
      )
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [dateFrom, dateTo])

  const vehicleOptions = useMemo(() => {
    const byId = new Map()
    checks.forEach((check) => {
      if (check.vehicle?._id) byId.set(check.vehicle._id, check.vehicle)
    })
    return [...byId.values()].sort((a, b) =>
      (a.callSign ?? '').localeCompare(b.callSign ?? ''),
    )
  }, [checks])

  const rows = vehicle
    ? checks.filter((check) => check.vehicle?._id === vehicle._id)
    : checks

  const chrome = usePrintChrome()

  const handlePrint = async () => {
    const withAdvisories = rows.filter(
      (check) => (check.testStats?.advisoryCount ?? 0) > 0,
    )
    await downloadPdf(
      <ListReportPDF
        title="Vehicle Safety Checks"
        subtitle={[
          `${dateFrom?.isValid?.() ? dateFrom.format('D MMM YYYY') : 'Any'} - ${
            dateTo?.isValid?.() ? dateTo.format('D MMM YYYY') : 'Any'
          }`,
          vehicle
            ? `Vehicle: ${[vehicle.callSign, vehicle.regPlate].filter(Boolean).join(' · ')}`
            : 'All vehicles',
        ].join(' · ')}
        docLabel="Fleet Report"
        {...chrome}
        stats={[
          { label: 'Safety checks', value: rows.length },
          {
            label: 'Passed',
            value: rows.length - withAdvisories.length,
            color: GOOD,
          },
          {
            label: 'With advisories',
            value: withAdvisories.length,
            color: withAdvisories.length ? WARN : undefined,
          },
        ]}
        columns={[
          { label: 'Date', flex: 0.6 },
          { label: 'Vehicle', flex: 0.8 },
          { label: 'Registration', flex: 0.7 },
          { label: 'Mileage', flex: 0.6 },
          { label: 'Tested By', flex: 1.2 },
          { label: 'Result', flex: 0.8 },
        ]}
        rows={rows.map((check) => {
          const advisories = check.testStats?.advisoryCount ?? 0
          return [
            fmtDate(check.testDate),
            check.vehicle?.callSign ?? 'Removed vehicle',
            check.vehicle?.regPlate ?? '',
            check.mileage ?? '-',
            testedByLabel(check),
            advisories > 0
              ? {
                  pill: {
                    label: `${advisories} ${advisories === 1 ? 'advisory' : 'advisories'}`,
                    color: WARN,
                  },
                }
              : { pill: { label: 'Passed', color: GOOD } },
          ]
        })}
        emptyText="No safety checks recorded in this period"
      />,
      `AmbuNet_Vehicle_Safety_Checks_${moment().format('YYYY-MM-DD')}.pdf`,
    )
  }

  return (
    <Stack gap={1}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="title2">Vehicle Safety Checks</Typography>
        {!disabled && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/fleet/record-safety-check')}
          >
            Record Safety Check
          </Button>
        )}
      </Stack>
      {disabled && (
        <Alert severity="info">
          Vehicle Safety Checks are switched off for your organisation, so new
          checks can't be recorded. They can be enabled in Site Configuration
          → Vehicles. Previously recorded checks are still listed below.
        </Alert>
      )}
      <DetailsBox title="Search/Filter">
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} lg={3}>
            <AnDateField
              label="From"
              value={dateFrom}
              onChange={(newDate) => setDateFrom(newDate)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <AnDateField
              label="To"
              value={dateTo}
              onChange={(newDate) => setDateTo(newDate)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Autocomplete
              options={vehicleOptions}
              value={vehicle}
              onChange={(e, newValue) => setVehicle(newValue)}
              getOptionLabel={(option) =>
                [option.callSign, option.regPlate].filter(Boolean).join(' · ')
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderInput={(params) => <TextField label="Vehicle" {...params} />}
              fullWidth
            />
          </Grid>
        </Grid>
      </DetailsBox>
      {fetchError ? (
        <Alert severity="error">Unable to load safety checks.</Alert>
      ) : (
        <CustomDataGrid
          rows={rows}
          loading={loading}
          hideFilters
          onPrint={handlePrint}
          getRowId={(row) => row._id}
          norowsoverlay={
            <Alert severity="info">
              No safety checks recorded in this period. Record one with the
              button above, or from a vehicle's Safety Checks tab.
            </Alert>
          }
          columns={[
            {
              field: 'testDate',
              headerName: 'Date',
              renderCell: ({ value }) => moment(value).format('DD/MM/YYYY'),
              minWidth: 110,
              flex: 0.5,
            },
            {
              field: 'callSign',
              headerName: 'Vehicle',
              valueGetter: (value, row) => row.vehicle?.callSign ?? 'Removed vehicle',
              minWidth: 120,
              flex: 0.75,
            },
            {
              field: 'regPlate',
              headerName: 'Registration',
              valueGetter: (value, row) => row.vehicle?.regPlate ?? '',
              minWidth: 110,
              flex: 0.5,
            },
            {
              field: 'mileage',
              headerName: 'Mileage',
              minWidth: 100,
              flex: 0.5,
            },
            {
              field: 'testedBy',
              headerName: 'Tested By',
              valueGetter: (value, row) => testedByLabel(row),
              minWidth: 180,
              flex: 1,
            },
            {
              field: 'result',
              headerName: 'Result',
              valueGetter: (value, row) => row.testStats?.advisoryCount ?? 0,
              renderCell: ({ value }) =>
                value > 0 ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${value} ${value === 1 ? 'advisory' : 'advisories'}`}
                  />
                ) : (
                  <Chip size="small" color="success" variant="outlined" label="Passed" />
                ),
              minWidth: 130,
              flex: 0.5,
            },
          ]}
          onRowClick={({ row }) => navigate('/fleet/vehicle-safety-check/' + row._id)}
          disableRowSelectionOnClick
          sx={{
            '.MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
          }}
        />
      )}
    </Stack>
  )
}

export default SafetyChecks
